
import { supabase } from '../client';
import { Product } from '@/lib/types';

/**
 * Migrate products from localStorage to Supabase
 * Using a more robust upsert approach to prevent data loss
 */
export const migrateProducts = async (userId: string, localProducts: Product[]) => {
  if (localProducts.length === 0) {
    console.log('No products to migrate');
    return { count: 0, success: true };
  }

  console.log(`Migrating ${localProducts.length} products`);
  const result = { count: 0, success: true };
  
  // First, get existing products from Supabase for this user
  try {
    const { data: existingRemoteProducts, error: fetchError } = await supabase
      .from('products')
      .select('*')
      .eq('user_id', userId);
      
    if (fetchError) {
      console.error('Error fetching existing products:', fetchError);
      return { count: 0, success: false };
    }
    
    // Create a map of existing remote products by ID for quick lookup
    const remoteProductsMap = new Map();
    existingRemoteProducts?.forEach(product => {
      remoteProductsMap.set(product.id, product);
    });
    
    console.log(`Found ${remoteProductsMap.size} existing products in Supabase`);
    
    // Process each local product
    for (const product of localProducts) {
      try {
        // Format product object for database
        const dbProduct = {
          id: product.id,
          user_id: userId,
          name: product.name,
          description: product.description,
          category: product.category,
          current_stock: product.currentStock,
          min_stock_alert: product.minStockAlert,
          usage_rate: product.usageRate,
          supplier_name: product.supplierName,
          supplier_website: product.supplierWebsite,
          supplier_contact: product.supplierContact,
          product_code: product.productCode,
          change_frequency: product.changeFrequency,
          next_change_date: product.nextChangeDate ? product.nextChangeDate.toISOString() : null,
          package_size: product.packageSize,
          created_at: product.createdAt.toISOString(),
          updated_at: product.updatedAt ? product.updatedAt.toISOString() : new Date().toISOString()
        };

        // Check if this product exists remotely
        const existingProduct = remoteProductsMap.get(product.id);
        
        // If product exists remotely, keep the newer version based on updated_at timestamp
        if (existingProduct) {
          const localUpdatedAt = new Date(product.updatedAt || product.createdAt);
          const remoteUpdatedAt = new Date(existingProduct.updated_at);
          
          // Only update if local is newer than remote
          if (localUpdatedAt > remoteUpdatedAt) {
            const { error: updateError } = await supabase
              .from('products')
              .update(dbProduct)
              .eq('id', product.id)
              .eq('user_id', userId);
              
            if (updateError) {
              console.error(`Error updating product ${product.name}:`, updateError);
              result.success = false;
              continue;
            }
            
            console.log(`Updated product in Supabase: ${product.name} (newer local version)`);
            result.count++;
          } else {
            console.log(`Kept existing product in Supabase: ${product.name} (remote version is newer)`);
            // Still count it as migrated since we made the right decision
            result.count++;
          }
        } else {
          // Product doesn't exist remotely, so insert it
          const { error: insertError } = await supabase
            .from('products')
            .insert(dbProduct);
            
          if (insertError) {
            console.error(`Error inserting product ${product.name}:`, insertError);
            result.success = false;
            continue;
          }
          
          console.log(`Inserted new product to Supabase: ${product.name}`);
          result.count++;
        }
      } catch (error) {
        console.error(`Error processing product ${product.name}:`, error);
        result.success = false;
      }
    }
  } catch (error) {
    console.error('Error during product migration:', error);
    result.success = false;
  }
  
  return result;
};
